// File: ./src/components/ModCalculatorPage.js
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import PageLayout from './PageLayout';
import ModUpgradeCalculator from './ModUpgradeCalculator';

const ModCalculatorPage = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const [mods, setMods] = useState([]);
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set document title and track page view
  useEffect(() => {
    document.title = `Mod Upgrade Calculator - ${t('app.title')}`;
    
    // Track page view in Google Analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'page_view', {
        'page_title': 'Mod Upgrade Calculator',
        'page_location': window.location.href,
        'calculator_type': 'mod'
      });
    }
  }, [t, location]);

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const baseUrl = window.location.origin;
        const [modsResponse, costsResponse] = await Promise.all([
          fetch(`${baseUrl}/mod_list.json`),
          fetch(`${baseUrl}/mod_cost.json`),
        ]);

        if (!modsResponse.ok || !costsResponse.ok) {
          throw new Error('Failed to load mod data files');
        }

        const [modsData, costsData] = await Promise.all([
          modsResponse.json(),
          costsResponse.json(),
        ]);

        setMods(modsData);
        setCosts(costsData);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">{t('loading.message')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">
            <strong className="font-bold">{t('error.title')}</strong>
            <span className="block sm:inline"> {error}</span>
          </div>
          <p className="text-gray-600 dark:text-gray-300">{t('error.message')}</p>
        </div>
      </div>
    );
  }

  return (
    <PageLayout
      title="Mod Upgrade Calculator"
      subtitle="Calculate the total mod parts needed from Level 1 to your target level"
      showLastUpdated={true}
    >
      <ModUpgradeCalculator mods={mods} costs={costs} />
    </PageLayout>
  );
};

export default ModCalculatorPage;

